import React, { FC, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useUnit } from 'effector-react';
import {
  $boards,
  $cards,
  $cardsPending,
  $columnsById,
  $isAllSelected,
  $selectedBoardIds,
  $selectedCardIds,
  clearSelectedCards,
  createExportTaskFx,
  DynamicObjectExportBoardOption,
  selectAllCards,
  setSelectedBoardIds,
  toggleCard,
} from './model';
import { getListBoardForPageFx } from '../../model/board';

export const DynamicObjectExportForm: FC = () => {
  const [
    boards,
    boardsPending,
    selectedBoardIds,
    cardsState,
    isAllSelected,
    selectedCardIds,
    createPending,
    cardsPending,
  ] = useUnit([
    $boards,
    getListBoardForPageFx.pending,
    $selectedBoardIds,
    $cards,
    $isAllSelected,
    $selectedCardIds,
    createExportTaskFx.pending,
    $cardsPending,
  ]);

  const columnsById = useUnit($columnsById);

  const [taskName, setTaskName] = useState<string>('');

  const boardTitleById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const b of boards) {
      map[b.uuid] = b.title;
    }
    return map;
  }, [boards]);

  const selectedBoards = useMemo((): DynamicObjectExportBoardOption[] => {
    return selectedBoardIds
      .map((id) => boards.find((b) => b.uuid === id))
      .filter((b): b is DynamicObjectExportBoardOption => b !== undefined);
  }, [boards, selectedBoardIds]);

  const handleBoardsAutocompleteChange = (
    _: React.SyntheticEvent,
    newValue: DynamicObjectExportBoardOption[],
  ) => {
    setSelectedBoardIds(newValue.map((b) => b.uuid));
    clearSelectedCards();
  };

  const handleToggleAllCards = () => {
    if (!cardsState || cardsState.rows.length === 0) return;
    if (isAllSelected) {
      clearSelectedCards();
    } else {
      selectAllCards();
    }
  };

  const handleCreateTask = async () => {
    if (selectedBoardIds.length === 0) return;
    if (!cardsState || cardsState.rows.length === 0) return;
    try {
      await createExportTaskFx({ name: taskName.trim() || undefined });
      setSelectedBoardIds([]);
      clearSelectedCards();
      setTaskName('');
    } catch {
      // ошибки обработает errorHandlerFx
    }
  };

  const disabledCreate =
    selectedBoardIds.length === 0 ||
    !cardsState ||
    cardsState.rows.length === 0 ||
    createPending;

  const hasCards = Boolean(cardsState && cardsState.rows.length > 0);

  const selectedCount = useMemo(
    () => selectedCardIds.size,
    [selectedCardIds],
  );

  const noBoardsInWorkspace = !boardsPending && boards.length === 0;

  return (
    <Box display="flex" flexDirection="column" gap={2} width="100%">
      <Typography variant="h6">Формирование отчёта</Typography>
      <Card sx={{ width: '100%' }}>
        <CardContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              id="dynamic-object-export-board-select"
              options={boards}
              disabled={boardsPending || noBoardsInWorkspace}
              value={selectedBoards}
              onChange={handleBoardsAutocompleteChange}
              getOptionLabel={(option) => option.title}
              isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
              
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Доски"
                  variant="standard"
                  helperText={noBoardsInWorkspace ? 'Доски не найдены' : undefined}
                  inputProps={{
                    ...params.inputProps,
                    id: 'dynamic-object-export-board-input',
                  }}
                />
              )}
            />

            <TextField
              label="Название задачи (необязательно)"
              variant="standard"
              value={taskName}
              onChange={(event) => setTaskName(event.target.value)}
              fullWidth
            />

            <Box>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">
                    Карточки с дополнительными полями
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {cardsPending && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={20} />
                      <Typography variant="body2">Загрузка карточек…</Typography>
                    </Box>
                  )}

                  {!cardsPending && !hasCards && selectedBoardIds.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Выберите одну или несколько досок.
                    </Typography>
                  )}

                  {!cardsPending && !hasCards && selectedBoardIds.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Нет карточек с дополнительными полями для выбранных досок.
                    </Typography>
                  )}

                  {hasCards && cardsState && (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox">
                              <Checkbox
                                indeterminate={
                                  selectedCount > 0 &&
                                  selectedCount < cardsState.rows.length
                                }
                                checked={isAllSelected}
                                onChange={handleToggleAllCards}
                              />
                            </TableCell>
                            <TableCell>Название</TableCell>
                            <TableCell>Доска</TableCell>
                            <TableCell>Колонка</TableCell>
                            <TableCell>Статус</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cardsState.rows.map((card) => (
                            <TableRow key={card.uuid} hover>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedCardIds.has(card.uuid)}
                                  onChange={() => toggleCard(card.uuid)}
                                />
                              </TableCell>
                              <TableCell>{card.title}</TableCell>
                              <TableCell>
                                {boardTitleById[card.board] ?? card.board}
                              </TableCell>
                              <TableCell>
                                {columnsById[card.column] ?? card.column}
                              </TableCell>
                              <TableCell>{card.status}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </AccordionDetails>
              </Accordion>
            </Box>

            <Box display="flex" alignItems="center" gap={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateTask}
                disabled={disabledCreate}
              >
                {createPending ? 'Создание…' : 'Создать отчёт'}
              </Button>
              {hasCards && (
                <Typography variant="body2" color="text.secondary">
                  {selectedCount > 0
                    ? `Выбрано карточек: ${selectedCount}. При выборе карточек отчёт формируется только по ним.`
                    : 'Карточки не выбраны — отчёт будет сформирован по всем выбранным доскам.'}
                </Typography>
              )}
              {!cardsPending && !hasCards && selectedBoardIds.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Нет карточек с дополнительными полями для выбранных досок.
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
